import { useState } from "react";
import { Zap, Wallet, Pause, Play, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, short, ago } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ModeSwitch = ({ mode, onSet, settings }) => {
  const [confirm, setConfirm] = useState(false);
  const btn = (m, label) => (
    <button
      data-testid={`mode-${m}-btn`}
      onClick={() => (m === "live" && mode !== "live" ? setConfirm(true) : onSet(m))}
      className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest transition-colors duration-200 ${
        mode === m ? (m === "live" ? "bg-[#EF4444] text-white" : "bg-[#6366F1] text-white") : "text-slate-400 hover:text-white"
      }`}
    >
      {label}
    </button>
  );
  return (
    <>
      <div className="flex border border-[#1E293B] bg-[#07090D]" data-testid="mode-switch">
        {btn("paper", "Paper")}
        {btn("live", "Live Mainnet")}
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-[#0B0F17] border-[#EF4444]/40 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Switch to live mainnet?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              The bot wallet will spend real SOL: ${settings?.trade_usd?.toFixed(2)} per copied trade,{" "}
              {settings?.trades_per_day ? `up to ${settings.trades_per_day} per day` : "with no daily limit"}, via Raydium or pump.fun. Meme coins are extremely
              risky and can go to zero. Only fund what you can afford to lose.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="live-confirm-cancel" className="rounded-none">Stay on paper</AlertDialogCancel>
            <AlertDialogAction data-testid="live-confirm-ok" className="rounded-none bg-[#EF4444] hover:bg-[#dc2626] text-white" onClick={() => onSet("live")}>
              Go live
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const Header = ({ settings, me, onLogout, refresh, tab, setTab }) => {
  const update = async (body) => {
    try {
      await api.post("/settings", body);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
    refresh();
  };
  const running = settings?.running && me.active;
  return (
    <header className="sticky top-0 z-50 bg-[#0B0F17]/85 backdrop-blur-xl border-b border-[#1E293B]">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3" data-testid="brand">
          <div className="relative w-9 h-9 bg-[#14F195] flex items-center justify-center">
            <Zap className="w-4 h-4 text-[#07090D] absolute -translate-x-1" strokeWidth={3} />
            <Zap className="w-4 h-4 text-[#9945FF] absolute translate-x-1" strokeWidth={3} />
          </div>
          <div>
            <div className="font-display text-xl font-extrabold tracking-tight leading-none">DUAL BOOST</div>
            <div className="text-[10px] font-mono tracking-[0.25em] text-slate-500">SOLANA MEME COPY ENGINE</div>
          </div>
          {me.is_owner && (
            <div className="ml-4 flex border border-[#1E293B]" data-testid="owner-tabs">
              {[["dashboard", "My bot"], ["owner", "Owner panel"]].map(([k, l]) => (
                <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                  className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest transition-colors ${tab === k ? "bg-[#14F195] text-[#07090D]" : "text-slate-400 hover:text-white"}`}>
                  {l}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 border border-[#1E293B] text-[11px] font-mono" data-testid="engine-status">
            <span className={`w-2 h-2 rounded-full ${running ? "bg-[#14F195] pulse-dot" : "bg-[#F59E0B]"}`} />
            <span className={running ? "text-[#14F195]" : "text-[#F59E0B]"}>{running ? "ENGINE 24/7" : me.active ? "PAUSED" : "INACTIVE"}</span>
            <span className="text-slate-500 hidden md:inline">· scan {ago(settings?.last_scan)}</span>
          </div>
          <ModeSwitch mode={settings?.mode} settings={settings} onSet={(mode) => update({ mode })} />
          <button data-testid="toggle-engine-btn" onClick={() => update({ running: !settings?.running })}
            className="p-2 border border-[#1E293B] hover:border-[#334155] hover:bg-[#111827] transition-colors" title={settings?.running ? "Pause" : "Resume"}>
            {settings?.running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button data-testid="logout-btn" onClick={onLogout} title="Log out"
            className="flex items-center gap-2 px-3 py-1.5 bg-[#9945FF]/15 border border-[#9945FF]/50 text-[12px] font-mono hover:bg-[#9945FF]/25 transition-colors">
            <Wallet className="w-3.5 h-3.5 text-[#9945FF]" />
            <span data-testid="current-user-label">{me.email || (me.wallet ? short(me.wallet) : me.name)}</span>
            <LogOut className="w-3 h-3 text-slate-400" />
          </button>
        </div>
      </div>
    </header>
  );
};
